/**
 * החברות שלי — the Tenant Workspace landing screen. 09 §5.1.
 *
 * This is a PARENT SCREEN (a list of rows of one entity kind), not a context selector. The context
 * SELECTOR is the dropdown in the context bar and there is exactly one of those. This screen exists
 * because "my companies" is also a place you go to look at things — it has search, it shows
 * lifecycle state per row, and it is where a denial's "חזרה לחברות שלי" lands.
 *
 * Binding rules from §5.1 implemented here:
 *   • Every row shows an EXPLICIT lifecycle state. Never inferred, never hidden on an inferred default.
 *   • A broken-identity row still appears, marked `דורש טיפול` — never omitted.
 *   • Search is on legal name and ח.פ. NEVER on a db code: a customer does not know their db code.
 *   • The list is the server's single "visible companies" answer. Nothing is filtered client-side
 *     for permission reasons — the picker never shows a company and then denies on click.
 */

import { useMemo, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { LifecycleChip } from '../components/LifecycleChip';
import type { Column } from '../components/DataTable';
import type { CompanySummary } from '../api/contract';
import { routes } from '../routes';

export function CompanyPickerScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim();
    if (q === '') return ctx.visibleCompanies;
    // Display-time search only. This narrows what is on screen; it never decides access.
    return ctx.visibleCompanies.filter(
      (c) => c.legalName.includes(q) || (c.registrationNumber ?? '').includes(q),
    );
  }, [ctx.visibleCompanies, query]);

  const columns: readonly Column<CompanySummary>[] = [
    {
      key: 'name',
      headerHe: t('company.name'),
      render: (c) => (
        <span className="company-cell">
          <span className="company-cell__name">{c.legalName}</span>
          {/* 09 §5.1: ח.פ as a muted second line. Never a code as the identity. */}
          <span className="company-cell__id" dir="ltr">
            {c.registrationNumber ?? t('company.noNumber')}
          </span>
        </span>
      ),
    },
    {
      key: 'lifecycle',
      headerHe: t('company.state'),
      render: (c) => <LifecycleChip status={c.lifecycleStatus} />,
    },
    {
      key: 'attention',
      headerHe: t('common.notes'),
      render: (c) =>
        c.needsAttention ? <span className="chip chip--attention">{t('company.needsAttention')}</span> : null,
    },
  ];

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={t('picker.title')}>
      {/*
        The office's own administration is a place, not a settings gear. OA-0001 §3.2 hands the
        customer the authorization system to OPERATE, and a system reachable only from a menu nobody
        opens is one the customer does not know they have.

        This is an affordance and never a control: a user the gate would refuse still sees the link
        and still gets a written Hebrew denial rather than a missing button and no explanation.
      */}
      <div className="actions-row">
        <a className="btn" href={routes.tenantAdmin}>
          {t('settings.title')}
        </a>
      </div>

      <label className="field field--inline">
        <span className="field__label">{t('picker.search')}</span>
        <input
          className="field__input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={t('picker.searchHint')}
        />
      </label>

      {/*
        THE READ, NOT THE LIST. **Phase 2 §6, vertical A.**

        This screen is the one place whose entire subject is *"which companies may I open"*, so it is
        the one place where an empty table and an unanswered read must not look alike. A user who
        opens GL-POINT and is told they have access to no companies — when in fact the read was
        refused or the server was unreachable — telephones the office to ask why their access was
        removed, and nobody removed anything.

        The read happens in `<App>`, because the context bar names the current company on every
        screen and 04 I6 allows the working context exactly one source. What arrives here is the
        RESULT of that one read.
      */}
      <ServerRead result={ctx.companiesResult} whatHe={t('picker.reading')} skeletonRows={8}>
        {() => (
          <DataTable<CompanySummary>
            captionHe={t('picker.title')}
            columns={columns}
            rows={rows}
            rowKey={(c) => c.companyUid}
            childHref={(c) => routes.companyHome(c.companyUid)}
            filterSummaryHe={
              query.trim() === ''
                // 09 §5.1 "313 is not 313": a count always states which N it is.
                ?                   `${t('picker.showing')}: ${String(ctx.visibleCompanies.length)}`
                : `${t('picker.showing')}: ${String(rows.length)} / ${String(ctx.visibleCompanies.length)} · "${query.trim()}"`
            }
            // Two different empties, and they are two different sentences. This one is about the
            // SEARCH; an office with no companies at all is the one below it.
            emptyStateHe={
              ctx.visibleCompanies.length === 0
                ? t('picker.empty')
                : t('picker.noMatch')
            }
          />
        )}
      </ServerRead>
    </AppShell>
  );
}
